import EmailIcon from '@mui/icons-material/Email';
import { Box, Typography, Stack } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { fileData } from '../../../../../reusable-components/file-thumbnail/utils';
import Markdown from '../../../../../reusable-components/markdown';
import { getUsers } from '../../../../../redux/slices/users';
import FileThumbnail from '../../../../../reusable-components/file-thumbnail';
import { cleanMailTo, replaceLinks, removeContentEditable } from '../../../../../reusable-components/markdown/markdown-cleanup';
import TicketUserAction from '../../TicketUserAction';
import FilePreviewModal from './FilePreviewModal';
import { getMessagesAndUserActionsSorted } from '../../../../../reusable-components/ChatBox/chatHelper';

export default function MobileTicketMessagesList() {
  const location = useLocation();
  const subticket = new URLSearchParams(location.search).get('subticket');
  const { id = '' } = useParams();
  const { users } = useSelector((state) => state.users.data);

  const { user } = useSelector((state) => state.user);
  const { allTicketUsers, subtickets } = useSelector(
    (state) => state.tickets.data.ticketInfoById[id] || {}
  ) || {};
  const { ticketMessages } = useSelector(
    (state) => state.tickets.data.ticketById[subticket || id] || {}
  );

  const [previewFile, setPreviewFile] = useState(null);

  const messagesAndUserActionsSorted = useMemo(() => {
    if(ticketMessages && (subtickets || allTicketUsers)) {
      const allUsers = subticket ? subtickets?.find((st) => st.id.toString() === subticket)?.allTicketUsers || [] : allTicketUsers;
      return getMessagesAndUserActionsSorted(ticketMessages, allUsers, 'ticket');
    }
    return [];
  }, [ticketMessages, allTicketUsers]);

  const dispatch = useDispatch();

  useEffect(() => {
    if(users.length === 0) {
      dispatch(getUsers());
    }
  }, [])

  const chatBubbleStyle = {
    padding: 2,
    width: '85%',
    fontSize: '14px',
    mt: 1,
    wordBreak: 'break-word',
  }

  const otherUserChatBubbleStyle = {
    ...chatBubbleStyle,
    borderRadius: '20px 20px 20px 0',
    backgroundColor: '#f4f4f4',
  }

  const currentUserChatBubbleStyle = {
    ...chatBubbleStyle,
    borderRadius: '20px 20px 0 20px',
    mr: 0,
    ml: 'auto',
    backgroundColor: '#f0f4f8'
  }

  const getUserName = (email) => {
    const user = users.find(user => user.email === email);
    return user ? `${user.firstName} ${user.lastName}` : email;
  }

  const formatSentOn = (sentOn) => {
    const sentDate = new Date(sentOn);
    const now = new Date();
    const wasMessageSentToday = sentDate.getFullYear() === now.getFullYear() &&
      sentDate.getMonth() === now.getMonth() &&
      sentDate.getDate() === now.getDate();
    if(wasMessageSentToday) {
      return sentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    return sentDate.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const FORMAT_IMG = ['jpg', 'jpeg', 'gif', 'bmp', 'png', 'svg'];

  const handleClosePreview = () => {
    setPreviewFile(null);
  };

  return (
    messagesAndUserActionsSorted?.map((item, idx) => {
      if (item.type === 'message') {
        return (
          <Box key={idx}>
            <Stack>
              <Box sx={user.email === item.sentBy ? currentUserChatBubbleStyle : otherUserChatBubbleStyle}>
                {!item.file ? <Markdown children={cleanMailTo(replaceLinks(removeContentEditable(item.note)))} /> :
                  <Box
                    onClick={() => setPreviewFile({name: item.note, id: item.id})}
                    sx={{
                      display: FORMAT_IMG.includes(fileData(item.note).type) ? 'block' : 'flex',
                      alignItems: 'center',
                      backgroundColor: '#ffffff',
                      borderRadius: 1,
                      padding: 0.3
                    }}
                >
                    <FileThumbnail
                      file={item.note}
                      tooltip
                      preview={`/api/TicketFile?noteId=${item.id}`}
                      displayImage={FORMAT_IMG.includes(fileData(item.note).type)}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: (theme) => theme.palette.grey[700],
                        ml: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                      {item.note}
                    </Typography>
                  </Box>}
                </Box>
                <Typography
                  variant="caption"
                  color="info.main"
                  sx={{
                    mr: user.email === item.sentBy ? 0 : '',
                    ml: user.email === item.sentBy ? 'auto' : '',
                  }}
                >
                {`${getUserName(item.sentBy)} ${formatSentOn(item.sentOn)}`}
                {item.emailMessage && (
                  <EmailIcon
                    sx={{ height: '14px', mb: -0.3, ml: 0.5, cursor: 'pointer' }}
                    color="secondary"
                  />
                )}
              </Typography>
            </Stack>
            <FilePreviewModal
              isDialogOpen={!!previewFile}
              handleDialogOpen={handleClosePreview}
              FORMAT_IMG={FORMAT_IMG}
              previewFile={previewFile}
            />
          </Box>
        );
      }
      if(item.type === 'addUser' || item.type === 'removeUser') return <TicketUserAction item={item} idx={idx} key={`${item.type}-${idx}`} />
      return null;
      
    })
  );
}